import {Injectable} from '@angular/core';
import {Action, Store} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {MetricsService} from './metrics.service';
import * as fromRoot from '../../../../state/app.state';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {FetchMetricsOptionsModel} from '../models/fetch-metrics-options.model';
import {MetricOptionModel} from '../../../../models/metric-option.model';
import {
  AddMetricValueFailure,
  AddMetricValueRequest,
  AddMetricValueSuccess,
  CheckMetricsValueExistsByDateFailure,
  CheckMetricsValueExistsByDateRequest,
  CheckMetricsValueExistsByDateSuccess,
  DeleteMetricValueFailure,
  DeleteMetricValueListFailure,
  DeleteMetricValueListRequest,
  DeleteMetricValueListSuccess,
  DeleteMetricValueRequest,
  DeleteMetricValueSuccess,
  EditMetricValueFailure,
  EditMetricValueListFailure,
  EditMetricValueListRequest,
  EditMetricValueListSuccess,
  EditMetricValueRequest,
  EditMetricValueSuccess,
  FetchMetricsDataFailure,
  FetchMetricsDataRequest,
  FetchMetricsDataSuccess,
  FetchMetricsOptionsFailure,
  FetchMetricsOptionsRequest,
  FetchMetricsOptionsSuccess
} from './metrics.actions';
import {CommonService} from '../../../shared/services/common.service';
import {FetchMetricValuesModel} from '../models/fetch-metric-values.model';
import {EditMetricValueModel} from '../models/edit-metric-value.model';
import {DeleteMetricValueModel} from '../models/delete-metric-value.model';
import {Metrics} from "../services/metrics";
import {
  DeleteMetricValueListModel
} from '../models/delete-metric-value-list.model';
import {EditMetricValueListModel} from '../models/edit-metric-value-list.model';

@Injectable()
export class MetricsEffects {

  fetchMetricsOptions$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchMetricsOptionsRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.participantService.fetchMetricsOptions(payload).pipe(
          map((response: FetchMetricsOptionsModel['response']) => {
            const {data, message} = response;
            const metricsOptions: MetricOptionModel[] = data.list;
            return FetchMetricsOptionsSuccess({metricsOptions, message});
          }),
          catchError((error) => {
            this.metricsService.fetchMetricsOptionsErrorMessage.next(error);
            return of(FetchMetricsOptionsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchMetricsOptionsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchMetricsOptionsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchMetrics$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchMetricsDataRequest),
      map((action) => {
        const payload: FetchMetricValuesModel['request'] = {
          metricId: action.payload.metricId,
          userId: action.payload.userId,
          pageNumber: action.payload.pageNumber,
          limit: action.payload.limit,
          combined: action.payload.combined,
          groupId: action.payload.groupId,
        }
        const extras = {
          code: action.payload.code
        }
        return {
          payload,
          extras,
        };
      }),
      mergeMap(({payload, extras}) =>
        this.participantService.fetchMetrics(payload).pipe(
          map((response) => {
            const {data, message} = response;
            return FetchMetricsDataSuccess({
              ...extras,
              data,
              message
            });
          }),
          catchError(() => {
            return of(FetchMetricsDataFailure({
              payload: {
                code: extras.code
              }
            }));
          }),
          tap((action: any) => {
            if (action.type === FetchMetricsDataSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchMetricsDataFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchMetricsByDate$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CheckMetricsValueExistsByDateRequest),
      map((action) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload) =>
        this.participantService.fetchMetrics(payload).pipe(
          map((response) => {
            const {data, message} = response;
            this.metricsService.updateMetricsValues.next(data.list);
            return CheckMetricsValueExistsByDateSuccess();
          }),
          catchError(() => {
            return of(CheckMetricsValueExistsByDateFailure());
          }),
          tap((action: any) => {
            if (action.type === CheckMetricsValueExistsByDateSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === CheckMetricsValueExistsByDateFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  addMetricValue$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(AddMetricValueRequest),
      map((action) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload) =>
        this.participantService.addMetricValue(payload).pipe(
          map((response) => {
            const {message} = response;
            return AddMetricValueSuccess({
              message
            });
          }),
          catchError(() => {
            return of(AddMetricValueFailure());
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === AddMetricValueSuccess.type) {
              this.commonService.notification(action.message, 'success');

              // close dialog
              this.commonService.closeDialog();

              // Fetch metric options and values after success
              this.store.dispatch(FetchMetricsOptionsRequest({
                payload: {
                  userId: payload.userId,
                  groupId: payload.groupId,
                }
              }))
            } else if (action.type === AddMetricValueFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  editMetricValue$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(EditMetricValueRequest),
      map((action) => {
        this.commonService.startLoading();
        return action;
      }),
      map((action) => {
        const payload: EditMetricValueModel['request'] = {
          valueId: action.payload.valueId,
          recordedOn: action.payload.recordedOn,
          value: action.payload.value,
        }
        const extras = action.payload.extras;
        return {
          payload,
          extras,
        };
      }),
      mergeMap(({payload, extras}) =>
        this.participantService.editMetricValue(payload).pipe(
          map((response) => {
            const {message} = response;
            return EditMetricValueSuccess({
              message
            });
          }),
          catchError(() => {
            return of(EditMetricValueFailure());
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === EditMetricValueSuccess.type) {
              this.commonService.notification(action.message, 'success');

              // close dialog
              this.commonService.closeDialog();

              // Fetch metric values after success
              this.store.dispatch(FetchMetricsDataRequest({
                payload: extras,
              }))
            } else if (action.type === EditMetricValueFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  deleteMetricValue$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteMetricValueRequest),
      map((action) => {
        this.commonService.startLoading();
        return action;
      }),
      map((action) => {
        const payload: DeleteMetricValueModel['request'] = {
          valueId: action.payload.valueId,
        }
        const extras = action.payload.extras;
        return {
          payload,
          extras,
        };
      }),
      mergeMap(({payload, extras}) =>
        this.participantService.deleteMetricValue(payload).pipe(
          map((response) => {
            const {message} = response;
            return DeleteMetricValueSuccess({
              message
            });
          }),
          catchError(() => {
            return of(DeleteMetricValueFailure());
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === DeleteMetricValueSuccess.type) {
              this.commonService.notification(action.message, 'success');

              // Fetch metric values after success
              this.store.dispatch(FetchMetricsDataRequest({
                payload: extras,
              }))
            } else if (action.type === DeleteMetricValueFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  deleteMetricValueList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteMetricValueListRequest),
      map((action) => {
        this.commonService.startLoading();
        return action;
      }),
      map((action) => {
        const payload: DeleteMetricValueListModel['request'] = {
          values: action.payload.values,
        }
        const extras = action.payload.extras;
        return {
          payload,
          extras,
        };
      }),
      mergeMap(({payload, extras}) =>
        this.participantService.deleteMetricValueList(payload).pipe(
          map((response) => {
            const {message} = response;
            return DeleteMetricValueListSuccess({
              message
            });
          }),
          catchError(() => {
            return of(DeleteMetricValueListFailure());
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === DeleteMetricValueListSuccess.type) {
              this.commonService.notification(action.message, 'success');

              // Fetch metric values after success
              this.store.dispatch(FetchMetricsDataRequest({
                payload: extras,
              }))
            } else if (action.type === DeleteMetricValueListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  editMetricValueList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(EditMetricValueListRequest),
      map((action) => {
        this.commonService.startLoading();
        return action;
      }),
      map((action) => {
        const payload: EditMetricValueListModel['request'] = {
          values: action.payload.values,
        }
        const extras = action.payload.extras;
        return {
          payload,
          extras,
        };
      }),
      mergeMap(({payload, extras}) =>
        this.participantService.editMetricValueList(payload).pipe(
          map((response) => {
            const {message} = response;
            return EditMetricValueListSuccess({
              message
            });
          }),
          catchError(() => {
            return of(EditMetricValueListFailure());
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === EditMetricValueListSuccess.type) {
              this.commonService.notification(action.message, 'success');

              // close dialog
              this.commonService.closeDialog();

              // Fetch metric values after success
              this.store.dispatch(FetchMetricsDataRequest({
                payload: extras,
              }))
            } else if (action.type === EditMetricValueListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private participantService: MetricsService,
    private actions: Actions,
    private commonService: CommonService,
    private metricsService: Metrics
  ) {
  }
}
